import React, { useState, useEffect } from 'react';
import { Camera, Loader2, Filter, Grid, List } from 'lucide-react';

const Gallery = ({
  wordpressUrl = 'https://dimensionias.com/cms',
  defaultCategory = 'gallery', // Default category slug
  showCategoryFilter = false, // Changed to false by default
  galleryOnly = true // New prop to show only gallery category
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryData, setGalleryData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory); // Changed from 'all' to defaultCategory
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch categories from WordPress
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?per_page=100`);
      if (!response.ok) throw new Error('Failed to fetch categories');

      const wpCategories = await response.json();

      if (galleryOnly) {
        // Only show gallery-related categories
        const galleryCategories = wpCategories.filter(cat =>
          cat.slug.includes('gallery') ||
          cat.name.toLowerCase().includes('gallery') ||
          cat.slug === defaultCategory
        );
        setCategories(galleryCategories.map(cat => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          count: cat.count
        })));
      } else {
        setCategories([
          { id: 'all', name: 'All Images', slug: 'all' },
          ...wpCategories.map(cat => ({
            id: cat.id,
            name: cat.name,
            slug: cat.slug,
            count: cat.count
          }))
        ]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  };

  // Fetch gallery items from WordPress
  const fetchGalleryItems = async (categorySlug = defaultCategory) => {
    try {
      setLoading(true);
      setError(null);

      // For gallery-only mode, always filter by the gallery category
      const targetCategory = galleryOnly ? defaultCategory : categorySlug;

      // Build API URL with category filter
      let apiUrl = `${wordpressUrl}/wp-json/wp/v2/posts?per_page=50&_embed=true&status=publish`;

      // Add category filter if not 'all' or if galleryOnly is true
      if (targetCategory !== 'all') {
        // First get the category ID by slug
        const categoryResponse = await fetch(`${wordpressUrl}/wp-json/wp/v2/categories?slug=${targetCategory}`);
        if (categoryResponse.ok) {
          const categoryData = await categoryResponse.json();
          if (categoryData.length > 0) {
            apiUrl += `&categories=${categoryData[0].id}`;
          } else {
            throw new Error(`Category "${targetCategory}" not found. Please create a category with slug "${targetCategory}" in WordPress.`);
          }
        }
      }

      console.log('Fetching from:', apiUrl);
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`Failed to fetch gallery items: ${response.status}`);

      const wpPosts = await response.json();
      console.log(`WordPress posts received from "${targetCategory}" category:`, wpPosts.length);

      if (wpPosts.length === 0 && targetCategory !== 'all') {
        throw new Error(`No posts found in the "${targetCategory}" category. Please add posts to this category in WordPress.`);
      }

      // Transform WordPress posts to gallery items
      const transformedGalleryData = wpPosts.map(post => {
        // Try to get featured image, fallback to first image in content
        let imageUrl = null;
        let imageAlt = post.title.rendered;

        // Check for featured image
        if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
          imageUrl = post._embedded['wp:featuredmedia'][0].source_url;
          imageAlt = post._embedded['wp:featuredmedia'][0].alt_text || post.title.rendered;
        } else {
          // Try to extract first image from post content
          const contentImageMatch = post.content.rendered.match(/<img[^>]+src="([^"]+)"/);
          if (contentImageMatch) {
            imageUrl = contentImageMatch[1];
          }
        }

        // If still no image, use a placeholder
        if (!imageUrl) {
          imageUrl = '/images/blogs/default.jpg'; // Fallback image
        }

        // Clean up description
        let description = post.excerpt.rendered.replace(/<[^>]*>/g, '').replace(/\[&hellip;\]/, '...');
        if (!description || description.length < 10) {
          const contentText = post.content.rendered.replace(/<[^>]*>/g, '');
          description = contentText.substring(0, 200) + (contentText.length > 200 ? '...' : '');
        }

        // Get categories
        const postCategories = post._embedded?.['wp:term']?.[0] || [];

        return {
          id: post.id,
          title: post.title.rendered,
          description: description || 'Gallery image',
          image: imageUrl,
          alt: imageAlt,
          date: post.date,
          slug: post.slug,
          link: post.link,
          categories: postCategories,
          content: post.content.rendered,
          wpPost: post // Keep original WordPress post for reference
        };
      });

      console.log('Transformed gallery data:', transformedGalleryData.length);
      setGalleryData(transformedGalleryData);

    } catch (err) {
      console.error('Error fetching gallery items:', err);
      setError(`Failed to load gallery items: ${err.message}`);

      // Fallback to sample data
      setGalleryData([
        {
          id: 1,
          title: '',
          description: '',
          image: '/images/classroom/class-1.jpg',
          alt: '',
          date: new Date().toISOString(),
          categories: [{ name: '' }]
        },
        {
          id: 2,
          title: '',
          description: '',
          image: '/images/classroom/class-5.jpg',
          alt: '',
          date: new Date().toISOString(),
          categories: [{ name: '' }]
        },
        {
          id: 3,
          title: '',
          description: '',
          image: '/images/faculty.png',
          alt: '',
          date: new Date().toISOString(),
          categories: [{ name: '' }]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    const initializeGallery = async () => {
      await fetchCategories();
      await fetchGalleryItems(selectedCategory);
    };
    initializeGallery();
  }, []);

  // Handle category change
  const handleCategoryChange = async (categorySlug) => {
    setSelectedCategory(categorySlug);
    await fetchGalleryItems(categorySlug);
  };

  const openModal = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedImage]);

  if (error && galleryData.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF7' }}>
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#134E4A' }}>
            Gallery Setup Required
          </h2>
          <p className="text-red-500 text-lg mb-6">{error}</p>
          <div className="bg-white p-6 rounded-lg shadow-lg mb-6" style={{ borderColor: '#E0F2F1', borderWidth: '1px' }}>
            <h3 className="font-bold mb-3" style={{ color: '#134E4A' }}>Quick Setup Steps:</h3>
            <ol className="text-left text-sm space-y-2" style={{ color: '#4A5568' }}>
              <li>1. Create a "gallery" category in WordPress Admin → Posts → Categories</li>
              <li>2. Create posts with images and assign them to the "gallery" category</li>
              <li>3. Set featured images for your posts (recommended)</li>
              <li>4. Make sure posts are published and public</li>
            </ol>
          </div>
          <button
            onClick={() => fetchGalleryItems(selectedCategory)}
            className="px-6 py-3 rounded-lg font-semibold text-white transition-colors"
            style={{ backgroundColor: '#134E4A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0FDF7' }}>
      {/* Main content with proper top padding for fixed navbar */}
      <div className="pt-20 sm:pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="relative inline-block">
            <h1
              className="text-5xl md:text-6xl font-bold mb-6 relative z-10"
              style={{ color: '#134E4A' }}
            >
              Gallery
              <div
                className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 rounded-full"
                style={{ backgroundColor: '#2DD4BF' }}
              ></div>
            </h1>
          </div>
          <p
            className="text-xl max-w-3xl mx-auto leading-relaxed"
            style={{ color: '#134E4A' }}
          >
            Explore our vibrant learning environment, dedicated faculty, and inspiring student achievements through our visual journey
          </p>
        </div>

        {/* Filters and View Toggle */}
        {showCategoryFilter && categories.length > 1 && (
          <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.slug)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${selectedCategory === category.slug
                    ? 'text-white shadow-lg'
                    : 'text-gray-600 bg-white border-2 hover:border-teal-400'
                    }`}
                  style={{
                    backgroundColor: selectedCategory === category.slug ? '#2DD4BF' : 'white',
                    borderColor: selectedCategory === category.slug ? '#2DD4BF' : '#E0F2F1'
                  }}
                >
                  <Filter size={16} className="inline mr-2" />
                  {category.name} {category.count && `(${category.count})`}
                </button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-white rounded-full p-2 shadow-md">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-3 rounded-full transition-colors ${viewMode === 'grid' ? 'text-white' : 'text-gray-400'
                  }`}
                style={{ backgroundColor: viewMode === 'grid' ? '#2DD4BF' : 'transparent' }}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-3 rounded-full transition-colors ${viewMode === 'list' ? 'text-white' : 'text-gray-400'
                  }`}
                style={{ backgroundColor: viewMode === 'list' ? '#2DD4BF' : 'transparent' }}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        )}

        {/* View Mode Toggle Only (when category filter is hidden) */}
        {!showCategoryFilter && (
          <div className="flex justify-end mb-12">
            <div className="flex bg-white rounded-full p-2 shadow-md">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-3 rounded-full transition-colors ${viewMode === 'grid' ? 'text-white' : 'text-gray-400'
                  }`}
                style={{ backgroundColor: viewMode === 'grid' ? '#2DD4BF' : 'transparent' }}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-3 rounded-full transition-colors ${viewMode === 'list' ? 'text-white' : 'text-gray-400'
                  }`}
                style={{ backgroundColor: viewMode === 'list' ? '#2DD4BF' : 'transparent' }}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin mr-2" style={{ color: '#2DD4BF' }} size={32} />
            <span className="text-lg" style={{ color: '#134E4A' }}>Loading gallery...</span>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-8 text-center">
              <p style={{ color: '#134E4A' }}>
                Showing <span className="font-bold">{galleryData.length}</span> image{galleryData.length !== 1 ? 's' : ''}
                {selectedCategory !== 'all' && (
                  <span> in <span className="font-bold">{categories.find(c => c.slug === selectedCategory)?.name}</span></span>
                )}
              </p>
            </div>

            {/* Gallery Grid/List */}
            <div className={
              viewMode === 'grid'
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                : "space-y-6"
            }>
              {galleryData.map((item) => (
                <div
                  key={`${item.id}-${item.slug}`}
                  className={`bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer group border-2 ${viewMode === 'list' ? 'flex' : ''
                    }`}
                  onClick={() => openModal(item)}
                  style={{ borderColor: '#E0F2F1' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#2DD4BF';
                    e.currentTarget.style.backgroundColor = '#FEFFFE';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#E0F2F1';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div className={`relative overflow-hidden ${viewMode === 'list' ? 'w-64 flex-shrink-0' : ''}`}>
                    <img
                      src={item.image}
                      alt={item.alt || item.title}
                      className={`object-cover transition-transform duration-500 group-hover:scale-110 ${viewMode === 'list' ? 'w-full h-48' : 'w-full h-72'
                        }`}
                      onError={(e) => {
                        e.target.src = '/images/blogs/default.jpg';
                      }}
                    />
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: 'linear-gradient(to top, rgba(45, 212, 191, 0.8), rgba(19, 78, 74, 0.3))'
                      }}
                    >
                      <div className="absolute bottom-6 left-6 text-white">
                        <div
                          className="p-3 rounded-full"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                        >
                          <Camera size={28} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-8 flex-1">
                    <h3
                      className="text-2xl font-bold mb-3 group-hover:text-teal-500 transition-colors duration-300"
                      style={{ color: '#134E4A' }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-base leading-relaxed mb-4"
                      style={{ color: '#4A5568' }}
                    >
                      {item.description}
                    </p>

                    {/* Categories */}
                    {item.categories && item.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {item.categories.slice(0, 3).map((cat, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#E0F2F1', color: '#134E4A' }}
                          >
                            {cat.name}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      {item.date && (
                        <div className="text-sm" style={{ color: '#6B7280' }}>
                          {new Date(item.date).toLocaleDateString()}
                        </div>
                      )}
                      <span
                        className="inline-flex items-center text-sm font-semibold group-hover:scale-105 transition-transform duration-200"
                        style={{ color: '#2DD4BF' }}
                      >
                        View Details
                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {galleryData.length === 0 && (
              <div className="text-center py-20">
                <Camera size={64} className="mx-auto mb-6" style={{ color: '#2DD4BF' }} />
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#134E4A' }}>
                  No Images Found
                </h3>
                <p className="text-lg mb-6" style={{ color: '#4A5568' }}>
                  {selectedCategory === 'all'
                    ? 'No gallery images are available at the moment.'
                    : `No images found in the "${categories.find(c => c.slug === selectedCategory)?.name}" category.`
                  }
                </p>
                {selectedCategory !== 'all' && (
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className="px-6 py-3 rounded-lg font-semibold text-white transition-colors"
                    style={{ backgroundColor: '#134E4A' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
                  >
                    View All Images
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal for enlarged image view */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="relative max-w-5xl max-h-full">
            <button
              onClick={closeModal}
              className="absolute -top-16 right-0 text-white hover:text-teal-400 transition-colors duration-200 p-2"
              style={{ backgroundColor: 'rgba(45, 212, 191, 0.2)', borderRadius: '50%' }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedImage.image}
              alt={selectedImage.alt || selectedImage.title}
              className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
              onError={(e) => {
                e.target.src = '/images/blogs/default.jpg';
              }}
            />
            <div
              className="mt-6 p-8 rounded-2xl shadow-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(240, 253, 247, 0.95), rgba(224, 242, 241, 0.95))',
                backdropFilter: 'blur(10px)'
              }}
            >
              <h3
                className="text-3xl font-bold mb-4"
                style={{ color: '#134E4A' }}
              >
                {selectedImage.title}
              </h3>
              <p
                className="text-lg leading-relaxed mb-4"
                style={{ color: '#4A5568' }}
              >
                {selectedImage.description}
              </p>

              {/* Categories in modal */}
              {selectedImage.categories && selectedImage.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedImage.categories.map((cat, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{ backgroundColor: 'rgba(45, 212, 191, 0.2)', color: '#134E4A' }}
                    >
                      {cat.name}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {selectedImage.date && (
                    <span className="text-sm" style={{ color: '#4A5568' }}>
                      {new Date(selectedImage.date).toLocaleDateString()}
                    </span>
                  )}
                  {selectedImage.link && (
                    <a
                      href={selectedImage.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm hover:underline"
                      style={{ color: '#2DD4BF' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Original Post
                    </a>
                  )}
                </div>
                <button
                  onClick={closeModal}
                  className="px-6 py-2 rounded-full font-semibold transition-colors duration-200"
                  style={{
                    backgroundColor: '#134E4A',
                    color: 'white',
                    border: 'none'
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#134E4A'}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;